import has from '@dojo/framework/core/has';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { create, v, w } from '@dojo/framework/core/vdom';
import { Button } from '@dojo/widgets/button';
import { RadioGroup } from '@dojo/widgets/radio-group';
import { DateInput } from '@holmescorp/base-widgets/date-input';
import { convertToBoolean } from '@holmescorp/common';
import { ParentTree } from '@holmescorp/lcc-interfaces';
import { logUser } from '@holmescorp/logger';
import { StudyPlanConfirmation } from '@holmescorp/ml';
import { numeral } from '@holmescorp/numeral';
import { XAPI } from '@holmescorp/xapi';
import { IStatement } from '@holmescorp/xapi/resources/istatement/statement';
import {
	assessmentChange,
	assessmentScore,
	createNlsMiddleware,
	documentapi,
	examdateChange,
	landingChange,
	lccstate,
	quizPatternChangeProcess,
	registry,
	userstate
} from 'lcc-state';
import { isEmpty } from 'lodash-es';
import { DateTime } from 'luxon';
import { publish } from 'pub-sub';
import css from '../css/info-gathering-view.m.css';

// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { DNode, WNodeFactory, WidgetProperties } from '@dojo/framework/core/interfaces';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ThemeProperties } from '@dojo/framework/core/middleware/theme';

export interface InfoGatheringViewProperties {}

interface InfoGatheringIcache {
	examinus_select: string | null;
	quizchoice_select: string | null;
	examdate_select: string;
	show_score_transfer: boolean;
	show_ml_recommendations_transfer: boolean;
	assessment_results: IStatement[] | undefined;
	assessment_scores: ParentTree | undefined;
}

const nls = createNlsMiddleware('ditabook');
const nlsSettings = createNlsMiddleware('settings');
const factory = create({
	icache: createICacheMiddleware<InfoGatheringIcache>(),
	theme,
	nls,
	nlsSettings,
	lccstate,
	userstate,
	documentapi
}).properties<InfoGatheringViewProperties>();

export const InfoGatheringView = factory(
	({ middleware: { icache, theme, nls, nlsSettings, lccstate, userstate, documentapi } }) => {
		const themeCss = theme.classes(css);

		const quizpatterns = lccstate.get(lccstate.path('quizpatterns'));
		const needsQuizChoice = quizpatterns && Object.keys(quizpatterns).length > 1;
		const user = lccstate.get(lccstate.path('actor_xapi'));

		const {
			messages: {
				info_gathering_title = '[ditabook] info_gathering_title',
				info_gathering_is_us_text = '[ditabook] info_gathering_is_us_text'
			}
		} = nls();

		const {
			messages: {
				include_exam_countdown = 'false',
				include_exam_in_us = 'true',
				fieldset_selectone = '[settings] fieldset_selectone',
				examcountdown_date = '[settings] examcountdown_date',
				examcountdown_intro = '[settings] examcountdown_intro',
				fieldset_examcountdown = '[settings] fieldset_examcountdown',
				transfer_assessment_notification = '',
				info_gathering_a11y_description = '[landing] info_gathering_a11y_description'
			}
		} = nlsSettings();

		const saveLandingComplete = () => {
			landingChange({ complete: true });
			publish('explore/active');
		};

		const assessmentResults = icache.get('assessment_results');
		const assessmentScores = icache.get('assessment_scores');

		// show ML confirm dialog after importing scores (only path that sets conditions to true)
		if (assessmentScores && icache.get('show_ml_recommendations_transfer')) {
			return w(StudyPlanConfirmation, { callback: saveLandingComplete });
		}

		// show intermediary content if importing assessment scores from SE1 product
		if (assessmentResults && assessmentResults.length && icache.get('show_score_transfer')) {
			const statement = assessmentResults[0];
			const percent = statement.result?.score?.scaled || 0;
			const scores = statement.result?.extensions as ParentTree;
			const isValid = scores && Object.keys(scores).length > 1;

			if (isValid) {
				icache.set('assessment_scores', scores, false);
				assessmentChange({ assessment: scores });
				assessmentScore({ score: `${numeral(percent * 0.01).format('0%')}` });
			}

			return v('div', { key: 'score-transfer-container', classes: [themeCss.root] }, [
				v('div', { innerHTML: transfer_assessment_notification }),
				v('div', { key: 'continue-transfer', classes: [themeCss.continue] }, [
					w(
						Button,
						{
							onClick: () => {
								if (has('use-ml-study-plan')) {
									icache.set('show_ml_recommendations_transfer', true);
								} else {
									saveLandingComplete();
								}
							}
						},
						['Continue']
					)
				])
			]);
		}

		// Exam in US Section
		const showIsInUS = convertToBoolean(include_exam_in_us);
		const examinus_select = icache.get('examinus_select');
		const inUs = v('div', { classes: [themeCss.misc] }, [
			w(
				RadioGroup,
				{
					name: 'examinus',
					initialValue: '',
					options: [
						{ label: 'Yes', value: 'true' },
						{ label: 'No', value: 'false' }
					],
					onValue: (value) => {
						icache.set('examinus_select', value);
						landingChange({ examinus: convertToBoolean(value) });
					}
				},
				[
					{
						label: info_gathering_is_us_text
					}
				]
			)
		]);

		// Quiz Select Section
		const values: string[] = quizpatterns ? Object.keys(quizpatterns).sort() : [];
		const qc_options = values.map((value) => {
			return { label: quizpatterns[value], value: value };
		});
		const quizchoice_select = icache.get('quizchoice_select');
		const showQuizChoice = !isEmpty(quizchoice_select);
		const quizchoice = v('div', { classes: [themeCss.misc] }, [
			w(
				RadioGroup,
				{
					name: 'quiz-select',
					initialValue: '',
					options: qc_options,
					onValue: (value) => {
						icache.set('quizchoice_select', value);
						userstate.run(quizPatternChangeProcess)({ quizPattern: value });
						publish('change:quizpattern', value);
						icache.set('assessment_results', async () => {
							const results = await XAPI.svc.get_statement({
								agent: JSON.stringify({ mbox: `${user.mbox}` }),
								activity: `se1:pre-test-${value}`,
								verb: XAPI.Verbs.COMPLETED.id,
								limit: '1'
							});
							// confirm the results match the current user before returning
							const statement = results[0];
							const actor = statement?.actor;
							const completed = statement?.result?.completion;
							const isValid = actor?.mbox === user.mbox && completed;
							return isValid ? results : [];
						});
					}
				},
				[
					{
						label: fieldset_selectone
					}
				]
			)
		]);

		// Exam Countdown Section
		const showExamDate = convertToBoolean(include_exam_countdown);
		const selectedDate = userstate.get(userstate.path('examdate'));
		const examdate_select = icache.getOrSet('examdate_select', selectedDate);
		const examcountdown = v('div', { classes: [themeCss.misc] }, [
			v('fieldset', {}, [
				v('legend', {}, [fieldset_examcountdown]),
				v('div', {}, [
					v('p', {}, [examcountdown_intro]),
					w(
						DateInput,
						{
							name: 'examCountdownSelectedDate',
							initialValue: examdate_select,
							onValue: (date: string) => {
								// const newDate = moment(date);
								const newDate = DateTime.fromISO(date);
								// if (newDate.diff(moment(), 'days', true) > 0) {
								if (newDate.diff(DateTime.now(), 'days').toMillis() > 0) {
									const examdate = newDate.toISODate() as string;
									logUser.info(() => `exam date selected: ${examdate}`);
									examdateChange({ examdate });
									icache.set('examdate_select', examdate);
								} else {
									icache.set('examdate_select', '');
								}
							}
						},
						[examcountdown_date]
					)
				])
			])
		]);

		const continueButton = v('div', { key: 'continue', classes: [themeCss.continue] }, [
			w(
				Button,
				{
					disabled:
						(showQuizChoice ? quizchoice_select : true) &&
						(showIsInUS ? examinus_select : true) &&
						(showExamDate ? examdate_select : true) &&
						(has('use-ml-study-plan') ? assessmentResults : true)
							? false
							: true,
					onClick: () => {
						if (assessmentResults && assessmentResults.length > 0 && transfer_assessment_notification) {
							icache.set('show_score_transfer', true);
						} else {
							saveLandingComplete();
						}
					}
				},
				['Continue']
			)
		]);

		const title = v('div', { innerHTML: info_gathering_title, classes: [themeCss.title] });

		documentapi.setTitle(info_gathering_a11y_description);

		return v('div', { classes: [themeCss.root] }, [
			v(
				'h1',
				{
					styles: {
						position: 'absolute',
						width: '1px',
						height: '1px',
						overflow: 'hidden',
						clip: 'rect(0,0,0,0)',
						whiteSpace: 'nowrap'
					}
				},
				[`${info_gathering_a11y_description}`]
			),
			title,
			showIsInUS ? inUs : null,
			needsQuizChoice ? quizchoice : null,
			showExamDate ? examcountdown : null,
			continueButton
		]);
	}
);

registry.define('LandingPageInfoGathering', InfoGatheringView);
export default InfoGatheringView;
